import {
  CommonModule,
} from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import {
  ColumnMode,
  DatatableComponent as NgxDatatableComponent,
  NgxDatatableModule,
  TableColumn,
} from "@swimlane/ngx-datatable";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { ScreenResizeService } from "src/utils/screen-resize.service";

type ScrollbarAxis = "horizontal" | "vertical";

interface ScrollbarDragState {
  axis: ScrollbarAxis;
  pointerId: number;
  railElement: HTMLElement;
  grabOffsetPx: number;
}

let nextDatatableBodyId = 0;

@Component({
    selector: "app-datatable",
    templateUrl: "./datatable.component.html",
    styleUrls: ["./datatable.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgxDatatableModule],
})
export class DatatableComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild("tableSummaryOrder", { static: true })
  tableSummaryOrder: TemplateRef<any>;

  @ViewChild("table", { read: ElementRef })
  tableElement?: ElementRef<HTMLElement>;

  @Input() mode = ColumnMode.force;
  @Input() limit = 50;
  @Input() externalSorting = true;
  @Input() enableSummary = false;
  @Input() showOrder = true;
  @Input() showFooter = true;
  @Input() horizontalScrollWhenEmpty = false;
  @Input() horizontalScrollByMinWidth = false;
  @Input() summaryPosition = "bottom";
  @Input() maxVisibleRows = 15;

  @Output() pageChange = new EventEmitter();
  @Output() sortChange = new EventEmitter();
  @Output() limitChange = new EventEmitter();

  @ViewChild("table")
  private ngxTable?: NgxDatatableComponent;

  public ColumnMode = ColumnMode;

  subscriptions: Subscription[] = [];
  columnsForDisplay: TableColumn[] = [];
  horizontalScroll = false;
  hasHorizontalOverflow = false;
  hasVerticalOverflow = false;
  horizontalScrollThumbWidthPercent = 100;
  horizontalScrollThumbTranslatePx = 0;
  horizontalScrollMax = 0;
  horizontalScrollValue = 0;
  verticalScrollThumbHeightPercent = 100;
  verticalScrollThumbTranslatePx = 0;
  verticalScrollRailTopPx = 0;
  verticalScrollRailHeightPx = 0;
  verticalScrollMax = 0;
  verticalScrollValue = 0;
  readonly bodyScrollId = `shared-datatable-body-${nextDatatableBodyId++}`;

  form: FormGroup;

  sources: any = [];

  loading: any;

  screenSize!: string;
  showMobileFooter = true;
  readonly loadingSkeletonRows = Array.from({ length: 5 }, (_, index) => index);

  options = [
    { value: 5 },
    { value: 10 },
    { value: 15 },
    { value: 20 },
    { value: 25 },
    { value: 50 },
    { value: 100 },
  ];

  private readonly defaultColumnWidth = 150;
  private readonly horizontalOverflowTolerance = 1;
  private readonly keyboardScrollStep = 40;
  private readonly orderColumnProps = new Set(["order", "index"]);
  private readonly actionColumnProps = new Set(["action", "actions", "option"]);
  private sourceColumns: unknown[] = [];
  private columnsContentWidth = 0;
  private columnsMinimumWidth = 0;
  private bodyElement?: HTMLElement;
  private observedRows: HTMLElement[] = [];
  private resizeObserver?: ResizeObserver;
  private bodyScrollElement?: HTMLElement;
  private bodyScrollListener?: () => void;
  private resetHorizontalScrollOnNextMeasurement = false;
  private measurementTimers = new Set<ReturnType<typeof setTimeout>>();
  private recalculateFrame?: number;
  private recalculateTimer?: ReturnType<typeof setTimeout>;
  private measurementRetryCount = 0;
  private readonly maxMeasurementRetries = 5;
  private readonly layoutRefreshDelays = [0, 50, 150];
  private externalLoading = false;
  private scrollbarDragState?: ScrollbarDragState;

  private scrRezService = inject(ScreenResizeService);
  private fb = inject(FormBuilder);

  constructor() {
    const initialScreenSize = localStorage.getItem("initScrSize");
    this.screenSize =
      initialScreenSize !== null ? JSON.parse(initialScreenSize) : "";
    setTimeout(() => {
      this.onScreenResize();
    }, 300);
  }

  @Input() set datasource(value: any) {
    if (value?.data !== undefined) {
      value.data.forEach((r: any, i: number) => {
        r.order = value.offset + (i + 1);
      });
      this.sources = value;
      if (!this.externalLoading) {
        this.loading = false;
      }
      this.resetHorizontalScrollOnNextMeasurement = true;
      this.refreshLayout();
    }
  }

  @Input() set isLoading(value: boolean) {
    this.externalLoading = value;
    this.loading = value;
    this.refreshLayout();
  }

  @Input() set columns(value: unknown[]) {
    this.sourceColumns = Array.isArray(value) ? value : [];
    this.refreshColumns();
    this.refreshLayout();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      limit: new FormControl(this.limit),
    });

    this.subscriptions.push(
      this.scrRezService.onResize$.pipe(debounceTime(250)).subscribe((res) => {
        this.screenSize = res;
        this.onScreenResize();
      }),
      this.form.valueChanges.subscribe((value) => {
        this.limitChange.emit(value);
      })
    );

    this.refreshColumns();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.refreshLayout();
      });

      if (this.tableElement) {
        this.resizeObserver.observe(this.tableElement.nativeElement);
      }
    }

    this.refreshLayout();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((i) => i.unsubscribe());
    this.resizeObserver?.disconnect();

    this.measurementTimers.forEach((timer) => clearTimeout(timer));
    this.measurementTimers.clear();

    if (this.recalculateFrame !== undefined && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(this.recalculateFrame);
    }

    if (this.recalculateTimer) {
      clearTimeout(this.recalculateTimer);
    }

    this.cancelScrollbarDrag();
    this.cleanupBodyScrollListener();
  }

  onPageChange(event: any) {
    this.loading = true;
    this.pageChange.emit({ paging: event });
  }

  onSortChange(event: any) {
    if (this.externalSorting) {
      this.loading = true;
    }

    this.sortChange.emit({ sort: event });
  }

  shouldShowEmptyRow(): boolean {
    return this.loading !== true && (this.sources.data?.length ?? 0) === 0;
  }

  shouldShowLoadingState(): boolean {
    return this.loading === true && (this.sources.data?.length ?? 0) === 0;
  }

  showHorizontalScrollAffordance(): boolean {
    return this.hasHorizontalOverflow &&
      this.hasHorizontalScrollContent() &&
      this.horizontalScrollMax > this.horizontalOverflowTolerance;
  }

  showVerticalScrollAffordance(): boolean {
    return this.hasVerticalOverflow && (this.sources.data?.length ?? 0) > 0;
  }

  horizontalScrollThumbTransform(): string {
    return `translateX(${this.horizontalScrollThumbTranslatePx}px)`;
  }

  verticalScrollThumbTransform(): string {
    return `translateY(${this.verticalScrollThumbTranslatePx}px)`;
  }

  isScrollbarDragging(axis: ScrollbarAxis): boolean {
    return this.scrollbarDragState?.axis === axis;
  }

  onScrollbarPointerDown(event: PointerEvent, axis: ScrollbarAxis): void {
    if (
      !event.isPrimary ||
      (event.pointerType === "mouse" && event.button !== 0)
    ) {
      return;
    }

    const railElement = event.currentTarget as HTMLElement | null;
    const body = this.getScrollableBody();

    if (!railElement || !body || this.getScrollMaximum(body, axis) <= 0) {
      return;
    }

    const thumbSelector =
      axis === "horizontal"
        ? ".shared-datatable-horizontal-affordance-thumb"
        : ".shared-datatable-vertical-affordance-thumb";
    const thumbElement = railElement.querySelector<HTMLElement>(thumbSelector);

    if (!thumbElement) {
      return;
    }

    const pointerPosition = this.getPointerPosition(event, axis);
    const thumbRect = thumbElement.getBoundingClientRect();
    const thumbStart = axis === "horizontal" ? thumbRect.left : thumbRect.top;
    const thumbSize = axis === "horizontal" ? thumbRect.width : thumbRect.height;
    const eventTarget = event.target as Node | null;
    const pointerStartedOnThumb =
      eventTarget === thumbElement ||
      (eventTarget !== null && thumbElement.contains(eventTarget));

    this.cancelScrollbarDrag();
    this.scrollbarDragState = {
      axis,
      pointerId: event.pointerId,
      railElement,
      grabOffsetPx: pointerStartedOnThumb
        ? pointerPosition - thumbStart
        : thumbSize / 2,
    };

    railElement.setPointerCapture(event.pointerId);
    event.preventDefault();

    if (!pointerStartedOnThumb) {
      this.updateScrollFromPointer(pointerPosition);
    }
  }

  onScrollbarPointerMove(event: PointerEvent): void {
    if (
      this.scrollbarDragState?.pointerId !== event.pointerId
    ) {
      return;
    }

    event.preventDefault();
    this.updateScrollFromPointer(
      this.getPointerPosition(event, this.scrollbarDragState.axis)
    );
  }

  onScrollbarPointerEnd(event: PointerEvent): void {
    if (this.scrollbarDragState?.pointerId !== event.pointerId) {
      return;
    }

    this.cancelScrollbarDrag();
  }

  onScrollbarKeydown(event: KeyboardEvent, axis: ScrollbarAxis): void {
    const body = this.getScrollableBody();

    if (!body) {
      return;
    }

    const currentOffset =
      axis === "horizontal" ? body.scrollLeft : body.scrollTop;
    const pageSize =
      axis === "horizontal" ? body.clientWidth : body.clientHeight;
    const maximum = this.getScrollMaximum(body, axis);
    let nextOffset: number | undefined;

    switch (event.key) {
      case "Home":
        nextOffset = 0;
        break;
      case "End":
        nextOffset = maximum;
        break;
      case "PageUp":
        nextOffset = currentOffset - pageSize;
        break;
      case "PageDown":
        nextOffset = currentOffset + pageSize;
        break;
      case "ArrowLeft":
        if (axis === "horizontal") {
          nextOffset = currentOffset - this.keyboardScrollStep;
        }
        break;
      case "ArrowRight":
        if (axis === "horizontal") {
          nextOffset = currentOffset + this.keyboardScrollStep;
        }
        break;
      case "ArrowUp":
        if (axis === "vertical") {
          nextOffset = currentOffset - this.keyboardScrollStep;
        }
        break;
      case "ArrowDown":
        if (axis === "vertical") {
          nextOffset = currentOffset + this.keyboardScrollStep;
        }
        break;
    }

    if (nextOffset === undefined) {
      return;
    }

    event.preventDefault();
    this.setBodyScrollOffset(body, axis, nextOffset);
  }

  onScreenResize() {
    if (["xs", "sm"].includes(this.screenSize)) {
      this.showMobileFooter = this.sources.total <= this.sources.limit;
    } else {
      this.showMobileFooter = true;
    }

    this.refreshLayout();
  }

  refreshLayout(): void {
    this.measurementRetryCount = 0;
    this.layoutRefreshDelays.forEach((delay) => this.scheduleBodyMeasurement(delay));
  }

  private refreshColumns(): void {
    this.columnsForDisplay = this.normalizeColumns(this.sourceColumns);
    this.columnsContentWidth = this.columnsForDisplay.reduce(
      (total, column) => total + this.getColumnWidth(column),
      0
    );
    this.columnsMinimumWidth = this.columnsForDisplay.reduce(
      (total, column) => total + this.getColumnMinimumWidth(column),
      0
    );
  }

  private getColumnWidth(column: TableColumn): number {
    return typeof column.width === "number" && Number.isFinite(column.width)
      ? column.width
      : this.defaultColumnWidth;
  }

  private getColumnMinimumWidth(column: TableColumn): number {
    return typeof column.minWidth === "number" && Number.isFinite(column.minWidth)
      ? column.minWidth
      : this.getColumnWidth(column);
  }

  private normalizeColumns(columns: unknown[]): TableColumn[] {
    const normalizedColumns = columns
      .filter((column): column is TableColumn => this.isTableColumn(column))
      .map((column) => this.lockColumnMovement(column));

    if (
      this.showOrder &&
      !normalizedColumns.some((column) => this.isOrderColumn(column))
    ) {
      normalizedColumns.unshift({
        prop: "order",
        name: "#",
        sortable: false,
        draggable: false,
        resizeable: false,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: "text-center",
        headerClass: "d-flex justify-content-center",
        ...(this.enableSummary
          ? { summaryTemplate: this.tableSummaryOrder }
          : {}),
      });
    }

    const orderColumnIndex = normalizedColumns.findIndex((column) =>
      this.isOrderColumn(column)
    );

    return normalizedColumns.map((column, index) => {
      if (
        Object.prototype.hasOwnProperty.call(column, "frozenLeft") ||
        Object.prototype.hasOwnProperty.call(column, "frozenRight")
      ) {
        return column;
      }

      if (this.isActionColumn(column)) {
        return { ...column, frozenRight: true };
      }

      if (orderColumnIndex >= 0 && index <= orderColumnIndex) {
        return { ...column, frozenLeft: true };
      }

      return column;
    });
  }

  private isOrderColumn(column: TableColumn): boolean {
    return (
      typeof column.prop === "string" && this.orderColumnProps.has(column.prop)
    );
  }

  private isActionColumn(column: TableColumn): boolean {
    return (
      typeof column.prop === "string" && this.actionColumnProps.has(column.prop)
    );
  }

  private isTableColumn(column: unknown): column is TableColumn {
    return typeof column === "object" && column !== null;
  }

  private scheduleBodyMeasurement(delay = 0): void {
    if (!this.tableElement) {
      return;
    }

    const timer = setTimeout(() => {
      this.measurementTimers.delete(timer);
      this.updateBodyViewport();
    }, delay);

    this.measurementTimers.add(timer);
  }

  private updateBodyViewport(): void {
    const table = this.tableElement?.nativeElement;
    const body = table?.querySelector<HTMLElement>(".datatable-body");

    if (!table) {
      return;
    }

    const tableMeasured = this.updateHorizontalScroll(table, body);

    if (tableMeasured) {
      this.scheduleNgxTableRecalculation();
    }

    if (!body) {
      this.horizontalScrollMax = 0;
      this.horizontalScrollValue = 0;
      this.hasVerticalOverflow = false;
      this.verticalScrollThumbHeightPercent = 100;
      this.verticalScrollThumbTranslatePx = 0;
      this.verticalScrollRailTopPx = 0;
      this.verticalScrollRailHeightPx = 0;
      this.verticalScrollMax = 0;
      this.verticalScrollValue = 0;
      this.cancelScrollbarDrag();
      this.cleanupBodyScrollListener();
      return;
    }

    body.id = this.bodyScrollId;

    if (this.resetHorizontalScrollOnNextMeasurement) {
      body.scrollLeft = 0;
      this.resetHorizontalScrollOnNextMeasurement = false;
    }

    const rows = Array.from(
      body.querySelectorAll<HTMLElement>(".datatable-row-wrapper")
    );
    const maxVisibleRows = this.getMaxVisibleRows();
    const visibleRows = rows.slice(0, maxVisibleRows);
    const shouldScroll =
      (this.sources.data?.length ?? rows.length) > maxVisibleRows &&
      visibleRows.length === maxVisibleRows;
    const rowsHeight = visibleRows.reduce(
      (height, row) =>
        height + (row.getBoundingClientRect().height || row.offsetHeight),
      0
    );

    if (shouldScroll && rowsHeight > 0) {
      const horizontalScrollbarHeight = Math.max(
        0,
        body.offsetHeight - body.clientHeight
      );

      table.classList.add("shared-datatable--scrollable");
      table.style.setProperty(
        "--shared-datatable-body-max-height",
        `${Math.ceil(rowsHeight + horizontalScrollbarHeight)}px`
      );
    } else {
      table.classList.remove("shared-datatable--scrollable");
      table.style.removeProperty("--shared-datatable-body-max-height");
    }

    this.updateVerticalScrollAffordance(body);
    this.syncBodyScroll(body);
    this.updateHorizontalScrollAffordance(body);
    this.observeBodyRows(body, visibleRows);
  }

  private getMaxVisibleRows(): number {
    const normalizedRows = Number(this.maxVisibleRows);

    return Number.isFinite(normalizedRows) && normalizedRows > 0
      ? Math.floor(normalizedRows)
      : 15;
  }

  private lockColumnMovement(column: TableColumn): TableColumn {
    return {
      ...column,
      draggable: false,
      resizeable: false,
    };
  }

  private getScrollableBody(): HTMLElement | undefined {
    return (
      this.bodyScrollElement ??
      this.tableElement?.nativeElement.querySelector<HTMLElement>(
        ".datatable-body"
      ) ??
      undefined
    );
  }

  private getPointerPosition(
    event: PointerEvent,
    axis: ScrollbarAxis
  ): number {
    return axis === "horizontal" ? event.clientX : event.clientY;
  }

  private getScrollMaximum(body: HTMLElement, axis: ScrollbarAxis): number {
    return axis === "horizontal"
      ? Math.max(0, this.getHorizontalScrollWidth(body) - body.clientWidth)
      : Math.max(0, body.scrollHeight - body.clientHeight);
  }

  private updateScrollFromPointer(pointerPosition: number): void {
    const dragState = this.scrollbarDragState;
    const body = this.getScrollableBody();

    if (!dragState || !body) {
      return;
    }

    const thumbSelector =
      dragState.axis === "horizontal"
        ? ".shared-datatable-horizontal-affordance-thumb"
        : ".shared-datatable-vertical-affordance-thumb";
    const thumbElement =
      dragState.railElement.querySelector<HTMLElement>(thumbSelector);

    if (!thumbElement) {
      return;
    }

    const railRect = dragState.railElement.getBoundingClientRect();
    const thumbRect = thumbElement.getBoundingClientRect();
    const railStart =
      dragState.axis === "horizontal" ? railRect.left : railRect.top;
    const railSize =
      dragState.axis === "horizontal" ? railRect.width : railRect.height;
    const thumbSize =
      dragState.axis === "horizontal" ? thumbRect.width : thumbRect.height;
    const maxThumbTranslate = Math.max(0, railSize - thumbSize);

    if (maxThumbTranslate <= 0) {
      return;
    }

    const thumbTranslate = this.clamp(
      pointerPosition - railStart - dragState.grabOffsetPx,
      0,
      maxThumbTranslate
    );
    const scrollMaximum = this.getScrollMaximum(body, dragState.axis);
    const scrollOffset =
      (thumbTranslate / maxThumbTranslate) * scrollMaximum;

    this.setBodyScrollOffset(body, dragState.axis, scrollOffset);
  }

  private setBodyScrollOffset(
    body: HTMLElement,
    axis: ScrollbarAxis,
    value: number
  ): void {
    const nextOffset = this.clamp(value, 0, this.getScrollMaximum(body, axis));

    if (axis === "horizontal") {
      body.scrollLeft = nextOffset;
    } else {
      body.scrollTop = nextOffset;
    }

    this.syncHeaderHorizontalOffset(body.scrollLeft, body.scrollTop);
    this.updateHorizontalScrollAffordance(body);
    this.updateVerticalScrollAffordance(body);
  }

  private clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(maximum, Math.max(minimum, value));
  }

  private cancelScrollbarDrag(axis?: ScrollbarAxis): void {
    const dragState = this.scrollbarDragState;

    if (!dragState || (axis !== undefined && dragState.axis !== axis)) {
      return;
    }

    if (dragState.railElement.hasPointerCapture(dragState.pointerId)) {
      dragState.railElement.releasePointerCapture(dragState.pointerId);
    }

    this.scrollbarDragState = undefined;
  }

  private updateHorizontalScroll(table: HTMLElement, body?: HTMLElement | null): boolean {
    const tableWidth =
      table.getBoundingClientRect().width || table.clientWidth;

    if (tableWidth <= 0) {
      if (this.measurementRetryCount < this.maxMeasurementRetries) {
        this.measurementRetryCount += 1;
        this.scheduleBodyMeasurement(50);
      }
      return false;
    }

    this.measurementRetryCount = 0;
    const isEmpty = (this.sources.data?.length ?? 0) === 0;
    const frame = table.closest<HTMLElement>(".shared-datatable-frame");
    let availableWidth = tableWidth;

    if (frame !== null) {
      const measuredFrameWidth = frame.getBoundingClientRect().width;

      if (measuredFrameWidth > 0) {
        availableWidth = measuredFrameWidth;
      } else if (frame.clientWidth > 0) {
        availableWidth = frame.clientWidth;
      }
    }

    const requiredColumnsWidth = this.horizontalScrollByMinWidth
      ? this.columnsMinimumWidth
      : this.columnsContentWidth;
    const columnsOverflow = requiredColumnsWidth > availableWidth;
    let shouldScroll = columnsOverflow;

    if (!this.horizontalScrollByMinWidth && !isEmpty) {
      shouldScroll ||= this.hasBodyHorizontalOverflow(body);
    }

    if (body) {
      this.updateEmptyHorizontalScrollSurface(
        body,
        isEmpty &&
          this.horizontalScrollWhenEmpty &&
          this.loading !== true &&
          shouldScroll
      );
    }

    this.hasHorizontalOverflow = shouldScroll;

    if (shouldScroll !== this.horizontalScroll) {
      this.horizontalScroll = shouldScroll;
      this.refreshColumns();
    }

    return true;
  }

  private hasBodyHorizontalOverflow(body?: HTMLElement | null): boolean {
    if (!body) {
      return false;
    }

    return body.scrollWidth > body.clientWidth + this.horizontalOverflowTolerance;
  }

  private updateHorizontalScrollAffordance(body: HTMLElement): void {
    if (!this.hasHorizontalOverflow || !this.hasHorizontalScrollContent()) {
      this.horizontalScrollThumbWidthPercent = 100;
      this.horizontalScrollThumbTranslatePx = 0;
      this.horizontalScrollMax = 0;
      this.horizontalScrollValue = 0;
      this.cancelScrollbarDrag("horizontal");
      return;
    }

    const clientWidth = body.clientWidth || body.offsetWidth;
    const scrollWidth = this.getHorizontalScrollWidth(body);

    if (clientWidth <= 0 || scrollWidth <= clientWidth) {
      this.horizontalScrollThumbWidthPercent = 100;
      this.horizontalScrollThumbTranslatePx = 0;
      this.horizontalScrollMax = 0;
      this.horizontalScrollValue = 0;
      this.cancelScrollbarDrag("horizontal");
      return;
    }

    const widthPercent = Math.min(
      100,
      Math.max(12, (clientWidth / scrollWidth) * 100)
    );
    const thumbWidth = (clientWidth * widthPercent) / 100;
    const maxScrollLeft = scrollWidth - clientWidth;
    const maxTranslate = Math.max(0, clientWidth - thumbWidth);
    const scrollRatio = maxScrollLeft > 0 ? body.scrollLeft / maxScrollLeft : 0;

    this.horizontalScrollMax = Math.round(maxScrollLeft);
    this.horizontalScrollValue = Math.round(
      this.clamp(body.scrollLeft, 0, maxScrollLeft)
    );
    this.horizontalScrollThumbWidthPercent = widthPercent;
    this.horizontalScrollThumbTranslatePx = Math.round(maxTranslate * scrollRatio);
  }

  private hasHorizontalScrollContent(): boolean {
    return (this.sources.data?.length ?? 0) > 0 ||
      (this.horizontalScrollWhenEmpty && this.loading !== true);
  }

  private getHorizontalScrollWidth(body: HTMLElement): number {
    return Math.max(body.scrollWidth, body.clientWidth);
  }

  private updateEmptyHorizontalScrollSurface(
    body: HTMLElement,
    shouldCreate: boolean
  ): void {
    const className = "shared-datatable-body--empty-horizontal-scroll";
    const widthProperty = "--shared-datatable-empty-scroll-width";

    if (!shouldCreate) {
      body.classList.remove(className);
      body.style.removeProperty(widthProperty);
      return;
    }

    body.classList.add(className);
    body.style.setProperty(
      widthProperty,
      `${Math.ceil(this.columnsContentWidth)}px`
    );
  }

  private updateVerticalScrollAffordance(body: HTMLElement): void {
    const clientHeight = body.clientHeight || body.offsetHeight;
    const scrollHeight = Math.max(body.scrollHeight, clientHeight);
    const hasOverflow =
      scrollHeight > clientHeight + this.horizontalOverflowTolerance;

    this.hasVerticalOverflow = hasOverflow;
    this.verticalScrollRailTopPx = body.offsetTop || 0;
    this.verticalScrollRailHeightPx = clientHeight;

    if (!hasOverflow || (this.sources.data?.length ?? 0) === 0) {
      this.verticalScrollThumbHeightPercent = 100;
      this.verticalScrollThumbTranslatePx = 0;
      this.verticalScrollMax = 0;
      this.verticalScrollValue = 0;
      this.cancelScrollbarDrag("vertical");
      return;
    }

    if (clientHeight <= 0 || scrollHeight <= clientHeight) {
      this.verticalScrollThumbHeightPercent = 100;
      this.verticalScrollThumbTranslatePx = 0;
      this.verticalScrollMax = 0;
      this.verticalScrollValue = 0;
      this.cancelScrollbarDrag("vertical");
      return;
    }

    const heightPercent = Math.min(
      100,
      Math.max(12, (clientHeight / scrollHeight) * 100)
    );
    const thumbHeight = (clientHeight * heightPercent) / 100;
    const maxScrollTop = scrollHeight - clientHeight;
    const maxTranslate = Math.max(0, clientHeight - thumbHeight);
    const scrollRatio = maxScrollTop > 0 ? body.scrollTop / maxScrollTop : 0;

    this.verticalScrollMax = Math.round(maxScrollTop);
    this.verticalScrollValue = Math.round(
      this.clamp(body.scrollTop, 0, maxScrollTop)
    );
    this.verticalScrollThumbHeightPercent = heightPercent;
    this.verticalScrollThumbTranslatePx = Math.round(maxTranslate * scrollRatio);
  }

  private scheduleNgxTableRecalculation(): void {
    if (!this.ngxTable || typeof this.ngxTable.recalculate !== "function") {
      return;
    }

    if (this.recalculateFrame !== undefined || this.recalculateTimer) {
      return;
    }

    const recalculate = () => {
      this.recalculateFrame = undefined;
      this.recalculateTimer = undefined;
      this.ngxTable?.recalculate();

      const body = this.getScrollableBody();

      if (body) {
        this.syncHeaderHorizontalOffset(body.scrollLeft, body.scrollTop);
        this.updateHorizontalScrollAffordance(body);
      }
    };

    if (typeof requestAnimationFrame !== "undefined") {
      this.recalculateFrame = requestAnimationFrame(recalculate);
      return;
    }

    this.recalculateTimer = setTimeout(recalculate, 0);
  }

  private syncBodyScroll(body: HTMLElement): void {
    if (!this.horizontalScroll && !this.hasVerticalOverflow) {
      this.cleanupBodyScrollListener();
      this.syncHeaderHorizontalOffset(0, body.scrollTop);
      return;
    }

    if (this.bodyScrollElement !== body) {
      this.cleanupBodyScrollListener();
      this.bodyScrollElement = body;
      this.bodyScrollListener = () => {
        this.syncHeaderHorizontalOffset(body.scrollLeft, body.scrollTop);
        this.updateHorizontalScrollAffordance(body);
        this.updateVerticalScrollAffordance(body);
      };
      body.addEventListener("scroll", this.bodyScrollListener, { passive: true });
    }

    this.syncHeaderHorizontalOffset(body.scrollLeft, body.scrollTop);
  }

  private syncHeaderHorizontalOffset(offsetX: number, offsetY = 0): void {
    const table = this.ngxTable as unknown as {
      onBodyScroll?: (event: { offsetX: number; offsetY: number }) => void;
    } | undefined;

    table?.onBodyScroll?.({ offsetX, offsetY });
  }

  private cleanupBodyScrollListener(): void {
    if (this.bodyScrollElement && this.bodyScrollListener) {
      this.bodyScrollElement.removeEventListener("scroll", this.bodyScrollListener);
    }

    this.bodyScrollElement = undefined;
    this.bodyScrollListener = undefined;
  }

  private observeBodyRows(body: HTMLElement, rows: HTMLElement[]): void {
    if (!this.resizeObserver) {
      return;
    }

    if (this.bodyElement !== body) {
      if (this.bodyElement) {
        this.resizeObserver.unobserve(this.bodyElement);
      }

      this.bodyElement = body;
      this.resizeObserver.observe(body);
    }

    const rowsChanged =
      rows.length !== this.observedRows.length ||
      rows.some((row, index) => row !== this.observedRows[index]);

    if (!rowsChanged) {
      return;
    }

    this.observedRows.forEach((row) => this.resizeObserver?.unobserve(row));
    this.observedRows = rows;
    this.observedRows.forEach((row) => this.resizeObserver?.observe(row));
  }
}
